import React from 'react'
import { connect } from 'react-redux'

import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import BackButtonApartment from '@pages/properties-view/apartments/v2/units-side-panel/back-button'
import BackButtonHouseAndLand from '@pages/properties-view/house-and-land/v2/lots-side-panel/back-button'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import {
  CloseSvg,
  Eye3SlashSvg,
  Eye3Svg,
  FullScreenSvg,
  StopCompareSvg,
} from '@svg/react'

import { PropertyType } from '@adUtilities/types'

interface ControlsProps {
  projectIdentity: ProjectIdentity
  propertyType: PropertyType
  fullscreen: boolean
  onStopComparison: () => void
  onFullScreenToggle: (status: boolean) => void
  showUnitInfo: boolean
  showLotInfo: boolean
}

function Controls({
  projectIdentity,
  propertyType,
  fullscreen,
  onStopComparison,
  onFullScreenToggle,
  showUnitInfo,
  showLotInfo,
}: ControlsProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })
  const showPropertyInfo = React.useMemo(() => {
    if (propertyType === PropertyType.Unit) {
      return showUnitInfo
    }
    if (propertyType === PropertyType.Lot) {
      return showLotInfo
    }
    return false
  }, [propertyType, showUnitInfo, showLotInfo])

  const handleTogglePropertyInfo = React.useCallback(() => {
    if (propertyType === PropertyType.Unit) {
      firebaseControlQuery.update({
        [`building.unitInfoPopup`]: !showUnitInfo,
      })
    }
    if (propertyType === PropertyType.Lot) {
      firebaseControlQuery.update({
        [`houseAndLand.lotInfoPopup`]: !showLotInfo,
      })
    }
  }, [propertyType, showUnitInfo, showLotInfo])

  const handleContainerClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
    },
    []
  )

  return (
    <div
      className="ml-2 flex flex-col items-start gap-[14px] text-xs font-semibold tracking-widest text-white"
      onClick={handleContainerClick}
      role="none"
    >
      {fullscreen && propertyType === PropertyType.Unit && (
        <BackButtonApartment
          className="rounded bg-[#747474] p-1.5"
          iconClasses="h-5 w-5 stroke-white !scale-[1] !rotate-180"
        />
      )}

      {fullscreen && propertyType === PropertyType.Lot && (
        <BackButtonHouseAndLand
          className="rounded bg-[#747474] p-1.5"
          iconClasses="h-5 w-5 stroke-white !scale-[1] !rotate-180"
        />
      )}

      <button
        type="button"
        onClick={() => onFullScreenToggle(!fullscreen)}
        className="rounded bg-[#747474] p-1.5"
      >
        {fullscreen ? (
          <CloseSvg className="h-5 w-5 !scale-[.8] text-white" />
        ) : (
          <FullScreenSvg className="h-5 w-5 text-white" />
        )}
      </button>

      <button
        type="button"
        onClick={handleTogglePropertyInfo}
        className="rounded bg-[#747474] p-1.5"
      >
        {showPropertyInfo ? (
          <Eye3SlashSvg className="h-5 w-5 stroke-white" />
        ) : (
          <Eye3Svg className="h-5 w-5 stroke-white" />
        )}
      </button>
      <button
        type="button"
        className="rounded bg-[#747474] p-1.5"
        onClick={onStopComparison}
      >
        <StopCompareSvg
          className="h-5 w-5 !scale-[.9] fill-white stroke-white"
          stroke={1.6}
        />
      </button>
    </div>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    projectIdentity,
    showUnitInfo: !!session?.building?.unitInfoPopup,
    showLotInfo: !!session?.houseAndLand?.lotInfoPopup,
  })
)(Controls)
